import pluralize from 'pluralize'
import toast from 'react-hot-toast'

import { showApiError, useMutation } from '@fv/client-core'
import type { ListShipment } from '@fv/client-types'

import { shipperFetch } from '@/utils/shipperFetch'

import { useUpdateCachedLoad } from './useUpdateCachedLoad'

async function cancelLoad(
  loadId?: string,
  isAllLoads?: boolean,
): Promise<ListShipment[]> {
  if (!loadId) throw new Error('`loadId` required to cancel load')

  return shipperFetch<ListShipment[]>(`/shipments/${loadId}`, {
    method: 'DELETE',
    query: { allRelatedLoads: !!isAllLoads },
  })
}

export function useCancelLoad(loadId?: string, showNotifications = true) {
  const updateCachedLoad = useUpdateCachedLoad()

  return useMutation((isAllLoads?: boolean) => cancelLoad(loadId, isAllLoads), {
    onSettled: (loads, error, isAllLoads) => {
      const loadString = pluralize('load', isAllLoads ? 2 : 1)

      if (error && showNotifications)
        return showApiError(`Unable to cancel ${loadString}`, error)

      loads?.forEach(updatedLoad => {
        updateCachedLoad({ updatedLoad })
      })

      showNotifications && toast.success(`Canceled ${loadString} successfully.`)
    },
  })
}

async function uncancelLoad(loadId: string): Promise<ListShipment> {
  if (!loadId) throw new Error('`loadId` required to cancel load')

  return shipperFetch<ListShipment>(`/shipments/${loadId}/uncancel`, {
    method: 'POST',
  })
}

export function useUnCancelLoad(loadId?: string, showNotifications = true) {
  const updateCachedLoad = useUpdateCachedLoad()

  return useMutation({
    mutationFn: () => uncancelLoad(loadId),
    onSettled: (load, error) => {
      if (error && showNotifications)
        return showApiError(`Unable to un-cancel load`, error)

      updateCachedLoad({ updatedLoad: load })

      showNotifications && toast.success(`Un-canceled load successfully.`)
    },
  })
}
